import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import WordGroups from './WordGroups';
import './ModulePage.css';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../contexts/LanguageContext';
import { updateCountsAfterGroupChange } from '../utils/countUpdates';
import { useModuleData } from '../hooks/useModuleData';
import { useWordGroupsData } from '../hooks/useWordGroupsData';
import { useData } from '../contexts/DataContext';
import { useIsClassroomHead } from '../hooks/useIsClassroomHead';

const ModulePage = ({ deleteModule }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { t, isRTL } = useLanguage();
  const [error, setError] = useState('');
  const [deletingModule, setDeletingModule] = useState(false);

  // Use DataContext hooks
  const { moduleData, loading: moduleLoading, error: moduleError, accessBlocked, refetch: refetchModuleData } = useModuleData(id);
  const { wordGroups, loading: groupsLoading, refetch: refetchWordGroups } = useWordGroupsData(moduleData?.id);
  const { refreshModules } = useData();
  const isHead = useIsClassroomHead(moduleData?.classroom_id);
  
  // Determine if user can edit/delete
  // For private modules (no classroom_id), user can always edit
  // For classroom modules, only heads can edit
  const canEdit = moduleData?.classroom_id ? (isHead === true) : true;



  const dataLoading = moduleLoading || groupsLoading;

  if (dataLoading) {
    return <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>{t('loading') || 'Loading...'}</div>;
  }

  // Show access blocked message if classroom subscription expired past grace period
  if (accessBlocked) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('accessDenied') || 'Access Denied'}</h2>
        <p>{moduleError || t('classroomSubscriptionExpired') || 'This classroom subscription has expired and the grace period has ended. Please contact your teacher to renew the subscription.'}</p>
        <Link to="/" className="btn btn-primary">{t('backToDashboard') || 'Back to Dashboard'}</Link>
      </div>
    );
  }

  if (!moduleData) return (
    <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
      <h2>{t('moduleNotFound')}</h2>
      <p>{error || moduleError || t('moduleNotFoundMessage')}</p>
      <Link to="/" className="btn btn-primary">{t('backToModules')}</Link>
    </div>
  );

  const handleDeleteWordGroup = async (groupId) => {
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId || !moduleData) {
        setError(t('pleaseLogIn'));
        return;
      }
      // Find the word group by ID and module_id
      const { data: groupData, error: groupError } = await supabase
        .from('word_groups')
        .select('*')
        .eq('module_id', moduleData.id)
        .eq('id', groupId)
        .single();
      
      if (groupError || !groupData) {
        setError(t('wordGroupNotFoundMessage'));
        return;
      }
      
      const groupPosition = groupData.position;
      
      // Delete all words with this group_id
      await supabase
        .from('words')
        .delete()
        .eq('group_id', groupData.id);
      // Delete the word group itself
      await supabase
        .from('word_groups')
        .delete()
        .eq('id', groupData.id);

      // Reorder remaining word groups to fill the gap
      const { data: remainingGroups, error: reorderError } = await supabase
        .from('word_groups')
        .select('*')
        .eq('module_id', moduleData.id)
        .gt('position', groupPosition)
        .order('position', { ascending: false });

      if (reorderError) {
        console.error('Error fetching remaining groups for reordering:', reorderError);
      } else if (remainingGroups && remainingGroups.length > 0) {
        // Decrement positions of all groups with higher positions
        const reorderPromises = remainingGroups.map(group =>
          supabase
            .from('word_groups')
            .update({ position: group.position - 1 })
            .eq('id', group.id)
        );

        await Promise.all(reorderPromises);

      }

      // Update module counts after group deletion
      await updateCountsAfterGroupChange(moduleData.id);

      // Refresh word groups data

      refetchWordGroups();

      // Refresh module data to update total word count

      refetchModuleData();

      // Refresh dashboard modules to update counts
      refreshModules();

      // If this is a classroom module, refresh classrooms list
      if (moduleData?.classroom_id) {
        window.dispatchEvent(new Event('refreshClassrooms'));
      }
    } catch (error) {
      setError(t('errorDeletingWordGroup') || 'Failed to delete word group. Please try again.');
    }
  };

  return (
    <>
      <div className="module-page-container">
        {error && (
          <div className="error-message" style={{ color: 'red', margin: '1rem 0', textAlign: 'center' }}>
            {error}
          </div>
        )}
        <button onClick={() => navigate('/')} className="btn btn-secondary btn-back">
          {isRTL ? '→' : '←'} {t('backToDashboard')}
        </button>
        <div className="module-page-header">
          <div>
            <h2>{t('module')}: {moduleData.name || (moduleData.from_lang === moduleData.to_lang
              ? t('definitions')
              : `${t(moduleData.from_lang)} ${isRTL ? '←' : '→'} ${t(moduleData.to_lang)}`)}</h2>
            {moduleData.name && (
              <p className="module-page-subtitle">
                {moduleData.from_lang === moduleData.to_lang
                  ? t('definitions')
                  : `${t(moduleData.from_lang)} ${isRTL ? '←' : '→'} ${t(moduleData.to_lang)}`}
              </p>
            )}
          </div>
          <div className="module-page-actions">
            {canEdit && (
              <>
                <button className="btn btn-primary" onClick={() => {
                  navigate(`/module/${moduleData.id}/learn-topic`);
                }}>{moduleData.from_lang === moduleData.to_lang ? t('addWords') : t('learnTopic')}</button>
                {deleteModule && (
                  <button 
                    className="btn btn-danger" 
                    onClick={async () => {
                      const confirmed = window.confirm(
                        t('areYouSureDelete') || 
                        `Are you sure you want to delete "${moduleData.name || t('thisModule')}"?`
                      );
                      if (confirmed) {
                        setDeletingModule(true);
                        const result = await deleteModule(moduleData.id);
                        if (!result) {
                          // If deletion was successful, refresh modules and navigate back
                          refreshModules();
                          
                          // If this is a classroom module, refresh classrooms list
                          if (moduleData?.classroom_id) {
                            window.dispatchEvent(new Event('refreshClassrooms'));
                            navigate(`/classroom/${moduleData.classroom_id}/view`);
                          } else {
                            navigate('/');
                          }
                        } else {
                          setError(result);
                          setDeletingModule(false);
                        }
                      }
                    }}
                    disabled={deletingModule}
                  >
                    {deletingModule ? (t('deleting') || 'Deleting...') : (t('delete') || 'Delete')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {wordGroups && wordGroups.length > 0 && (
          <div className="module-review-section">
            <div className="card module-review-card">
              <div className="module-review-card-header">
                <h3>{t('reviewModes') || 'Review Modes'}</h3>
                <p className="module-review-card-subtitle">
                  {t('reviewModesSubtitle') || 'Each mode below focuses on a different type of review:'}
                </p>
                <ul className="module-review-card-list">
                  <li>
                    <strong>{t('reviewAllDueWordsLabel') || 'Review All Due Words'}:</strong>{' '}
                    {t('reviewAllDueWordsDescription') || 'SM2 review of words whose review time is due now.'}
                  </li>
                  <li>
                    <strong>{t('learnUnmasteredWords') || 'Learn Unmastered Words'}:</strong>{' '}
                    {t('learnUnmasteredWordsDescription') || 'Learning-mode review of words that are not yet mastered.'}
                  </li>
                  <li>
                    <strong>{t('reviewWeakWords') || 'Review Weak Words'}:</strong>{' '}
                    {t('reviewWeakWordsDescription') || 'Focused review of words you often miss or struggle with.'}
                  </li>
                  <li>
                    <strong>{t('reviewForgottenWords') || 'Review Forgotten Words'}:</strong>{' '}
                    {t('reviewForgottenWordsDescription') || 'Review words you have not seen in a long time.'}
                  </li>
                  <li>
                    <strong>{t('allWordsLearningMode') || 'All Words Learning Mode'}:</strong>{' '}
                    {t('allWordsLearningModeDescription') || 'Endless learning-mode review of every card.'}
                  </li>
                  <li>
                    <strong>{t('randomWordReview') || 'Random Word Review'}:</strong>{' '}
                    {t('randomWordReviewDescription') || 'Endless review with a randomly selected card each time.'}
                  </li>
                </ul>
              </div>
              <div className="module-review-grid">
                <Link
                  to={`/module/${moduleData.id}/study-all`}
                  className="btn btn-primary review-mode-button"
                >
                  {t('reviewAllDueWordsLabel') || 'Review All Due Words'}
                </Link>
                <Link
                  to={`/module/${moduleData.id}/learn-unmastered`}
                  className="btn btn-primary review-mode-button"
                >
                  {t('learnUnmasteredWords')}
                </Link>
                <Link
                  to={`/module/${moduleData.id}/review-weak`}
                  className="btn btn-primary review-mode-button"
                >
                  {t('reviewWeakWords')}
                </Link>
                <Link
                  to={`/module/${moduleData.id}/review-forgotten`}
                  className="btn btn-primary review-mode-button"
                >
                  {t('reviewForgottenWords')}
                </Link>
                <Link
                  to={`/module/${moduleData.id}/learn-all`}
                  className="btn btn-primary review-mode-button"
                >
                  {t('allWordsLearningMode') || 'All Words Learning Mode'}
                </Link>
                <Link
                  to={`/module/${moduleData.id}/review-random`}
                  className="btn btn-primary review-mode-button"
                >
                  {t('randomWordReview') || 'Random Word Review'}
                </Link>
              </div>
            </div>
          </div>
        )}
        <WordGroups module={{ ...moduleData, word_groups: wordGroups }} onDelete={canEdit ? handleDeleteWordGroup : null} />
      </div>
    </>
  );
};

export default ModulePage;
